import { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  Divider,
  Chip,
  Modal,
  Backdrop,
  Fade,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Popper,
  ClickAwayListener,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import { useSocket, Message } from '../hooks/useSocket';
import { apiRequest } from '../utils/api';

interface ChatPanelProps {
  jobId: number;
  currentUserId: number;
}

export default function ChatPanel({ jobId, currentUserId }: ChatPanelProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Set<number>>(new Set());
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editText, setEditText] = useState<string>('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteMessageId, setDeleteMessageId] = useState<number | null>(null);
  const [participants, setParticipants] = useState<any[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionAnchorEl, setMentionAnchorEl] = useState<HTMLElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);

  const {
    isConnected,
    joinJob,
    leaveJob,
    sendMessage: sendSocketMessage,
    onMessage,
    onMessageUpdated,
    onMessageDeleted,
    onTyping,
    onError,
    emitTyping,
  } = useSocket();

  // Helper function to process a file (used by file input, drag-drop, and paste)
  const processFile = useCallback((file: File) => {
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    // Store the file object
    setSelectedFile(file);

    // Create preview URL only for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setFilePreview(previewUrl);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  }, []);

  // Load initial messages
  useEffect(() => {
    loadMessages();
  }, [jobId]);

  // Load all users once on mount (for mentions)
  useEffect(() => {
    loadParticipants();
  }, []);

  // Handle paste events for clipboard images and files
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      // Check for files/images in clipboard
      let hasFile = false;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        // Handle image files (including screenshots)
        if (item.type.indexOf('image') !== -1) {
          hasFile = true;
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            processFile(file);
          }
          return;
        }
        
        // Handle other file types
        if (item.kind === 'file') {
          hasFile = true;
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            processFile(file);
          }
          return;
        }
      }

      // If we found a file, prevent default text paste behavior
      // Otherwise, allow normal text paste to work
      if (hasFile) {
        e.preventDefault();
      }
    };

    // Add paste event listener to the document
    // This allows pasting anywhere when the chat is visible
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [processFile]);

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone (not just moving to a child element)
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Only process the first file
      const file = files[0];
      processFile(file);
    }
  };

  // Load all users for mentions
  const loadParticipants = async () => {
    try {
      const response = await apiRequest('/users', {
        method: 'GET',
      });
      // Transform users data to match participant format
      const users = Array.isArray(response.data) ? response.data : [];
      console.log('Loaded users for mentions:', users.length, 'users'); // Debug log
      
      const mappedUsers = users.map((user: any) => ({
        user_id: user.id,
        name: user.username || user.name || (user.email ? user.email.split('@')[0] : 'Unknown'),
        email: user.email || '',
        user_role: user.role || '',
      }));
      
      setParticipants(mappedUsers);
      console.log('Mapped participants:', mappedUsers.length); // Debug log
    } catch (error) {
      console.error('Error loading users:', error);
      // Set empty array on error to prevent issues
      setParticipants([]);
    }
  };

  // Socket connection and event handlers
  useEffect(() => {
    if (!isConnected) return;

    // Join job room
    joinJob(jobId);

    // Handle new messages
    const handleNewMessage = (message: Message) => {
      setMessages((prev) => {
        // Avoid duplicates by checking ID
        if (prev.some((m) => m.id === message.id)) {
          return prev;
        }
        // Remove optimistic/temporary message if this is the confirmed version
        // Match by: same sender, same content, same job, and temporary ID (large timestamp) or within 3 seconds
        const filtered = prev.filter((m) => {
          // Keep if it's not from the same sender
          if (m.sender_id !== message.sender_id) return true;
          // Keep if it's not from the same job
          if (m.job_id !== message.job_id) return true;
          // Keep if content doesn't match
          if (m.message_text !== message.message_text) return true;
          if (m.image_url !== message.image_url) return true;
          // If it matches, check if it's a temporary message (ID is a large timestamp) or within 3 seconds
          const isTemporaryId = m.id > 1000000000000; // Timestamp IDs are > 1 trillion
          const timeDiff = Math.abs(new Date(m.created_at).getTime() - new Date(message.created_at).getTime());
          // Remove if it's a temporary ID or if time difference is less than 3 seconds
          return !isTemporaryId && timeDiff > 3000;
        });
        return [...filtered, message];
      });
      scrollToBottom();
    };

    // Handle typing indicators
    const handleTyping = (data: { userId: number; jobId: number; isTyping: boolean }) => {
      if (data.jobId !== jobId || data.userId === currentUserId) return;

      setTypingUsers((prev) => {
        const newSet = new Set(prev);
        if (data.isTyping) {
          newSet.add(data.userId);
        } else {
          newSet.delete(data.userId);
        }
        return newSet;
      });

      // Clear typing indicator after 3 seconds
      if (data.isTyping) {
        const existingTimeout = typingTimeoutRef.current.get(data.userId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        const timeout = setTimeout(() => {
          setTypingUsers((prev) => {
            const newSet = new Set(prev);
            newSet.delete(data.userId);
            return newSet;
          });
          typingTimeoutRef.current.delete(data.userId);
        }, 3000);

        typingTimeoutRef.current.set(data.userId, timeout);
      }
    };

    // Handle message updates
    const handleMessageUpdated = (message: Message) => {
      setMessages((prev) => {
        return prev.map((m) => (m.id === message.id ? message : m));
      });
    };

    // Handle message deletions
    const handleMessageDeleted = (data: { messageId: number }) => {
      setMessages((prev) => {
        // Only remove the message with the exact matching ID
        return prev.filter((m) => m.id !== data.messageId);
      });
    };

    // Handle errors
    const handleError = (error: { message: string; error?: string }) => {
      console.error('Socket error:', error);
      // Show user-friendly error message
      if (error.message.includes('Access denied') || error.message.includes('Job not found')) {
        console.warn(`⚠️ Cannot join job ${jobId}: ${error.message}`);
      }
    };

    onMessage(handleNewMessage);
    onMessageUpdated(handleMessageUpdated);
    onMessageDeleted(handleMessageDeleted);
    onTyping(handleTyping);
    onError(handleError);

    return () => {
      leaveJob(jobId);
      // Clear typing timeouts
      typingTimeoutRef.current.forEach((timeout) => clearTimeout(timeout));
      typingTimeoutRef.current.clear();
    };
  }, [isConnected, jobId, currentUserId, joinJob, leaveJob, onMessage, onMessageUpdated, onMessageDeleted, onTyping, onError]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/jobs/${jobId}/messages?limit=100`, {
        method: 'GET',
      });
      setMessages(response.data.messages || []);
      scrollToBottom();
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async () => {
    const hasText = newMessage.trim();
    const hasFile = selectedFile;
    
    if ((!hasText && !hasFile) || sending) return;

    const messageText = newMessage.trim();
    const file = selectedFile;
    
    // Clear input fields immediately
    const tempId = Date.now();
    setNewMessage('');
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setSending(true);

    try {
      // Always use REST API for file uploads (socket can't handle files)
      // For text-only messages, use socket if connected
      if (file || !isConnected) {
        // Use REST API for file uploads or when socket is disconnected
        const formData = new FormData();
        formData.append('messageText', messageText);
        if (file) {
          formData.append('image', file); // Backend still expects 'image' field name
        }

        try {
          const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

          const response = await fetch(`${API_BASE_URL}/jobs/${jobId}/messages`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
          });

          if (!response.ok) {
            throw new Error('Failed to send message');
          }

          const data = await response.json();
          
          // Don't add message from API response if socket is connected
          // The backend will broadcast it via socket, and handleNewMessage will add it
          // Only add it if socket is disconnected (fallback)
          if (data?.message && !isConnected) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === data.message.id)) {
                return prev;
              }
              return [...prev, data.message];
            });
            scrollToBottom();
          }
        } catch (apiError) {
          console.error('Error sending message via API:', apiError);
          alert('Failed to send message. Please try again.');
        }
      } else {
        // Text-only message with socket connected - use socket
        const tempMessage: Message = {
          id: tempId, // Temporary ID
          job_id: jobId,
          sender_id: currentUserId,
          message_text: messageText,
          message_type: 'text',
          image_url: null,
          read_at: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, tempMessage]);
        scrollToBottom();

        // Send via socket
        sendSocketMessage(jobId, messageText, 'text');
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (showMentions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((prev) => {
          const filtered = getFilteredParticipants();
          return prev < filtered.length - 1 ? prev + 1 : prev;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((prev) => (prev > 0 ? prev - 1 : 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        insertMention();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        return;
      }
    }
    
    if (e.key === 'Enter' && !e.shiftKey && !showMentions) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getFilteredParticipants = () => {
    if (!mentionQuery) {
      return participants.filter((p) => p.user_id !== currentUserId);
    }
    const query = mentionQuery.toLowerCase();
    return participants.filter(
      (p) =>
        p.user_id !== currentUserId &&
        (p.name?.toLowerCase().includes(query) ||
          p.email?.toLowerCase().includes(query))
    );
  };

  const insertMention = (selectedIndex?: number) => {
    const filtered = getFilteredParticipants();
    const indexToUse = selectedIndex !== undefined ? selectedIndex : mentionIndex;
    if (filtered.length === 0 || indexToUse >= filtered.length || indexToUse < 0) return;

    const selectedUser = filtered[indexToUse];
    const mentionText = `@${selectedUser.name || selectedUser.email} `;
    
    // Find the last @ symbol position
    const cursorPos = textFieldRef.current?.selectionStart || newMessage.length;
    const textBeforeCursor = newMessage.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // Replace from @ to cursor position with the mention
      const textAfterCursor = newMessage.substring(cursorPos);
      const newText =
        newMessage.substring(0, lastAtIndex) + mentionText + textAfterCursor;
      setNewMessage(newText);
      setShowMentions(false);
      setMentionQuery('');
      setMentionIndex(0);
      
      // Set cursor position after the mention
      setTimeout(() => {
        const newCursorPos = lastAtIndex + mentionText.length;
        textFieldRef.current?.setSelectionRange(newCursorPos, newCursorPos);
        textFieldRef.current?.focus();
      }, 0);
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewMessage(value);
    handleTyping();

    // Check for @ mention
    const cursorPos = e.target.selectionStart || value.length;
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Check if there's a space after @ (meaning mention is complete)
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        // Show mentions dropdown
        const query = textAfterAt.toLowerCase();
        setMentionQuery(query);
        setShowMentions(true);
        setMentionIndex(0);
        setMentionAnchorEl(e.target as HTMLElement);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const handleTyping = () => {
    if (!isConnected) return;
    emitTyping(jobId, true);

    // Clear existing timeout
    const existingTimeout = typingTimeoutRef.current.get(currentUserId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set timeout to stop typing indicator
    const timeout = setTimeout(() => {
      emitTyping(jobId, false);
      typingTimeoutRef.current.delete(currentUserId);
    }, 1000);

    typingTimeoutRef.current.set(currentUserId, timeout);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getInitials = (name?: string) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleEditMessage = (message: Message) => {
    setEditingMessageId(message.id);
    setEditText(message.message_text || '');
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditText('');
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId) return;

    try {
      const response = await apiRequest(`/messages/${editingMessageId}`, {
        method: 'PUT',
        body: JSON.stringify({ messageText: editText }),
      });

      if (response.data?.message) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === editingMessageId ? response.data.message : msg
          )
        );
        setEditingMessageId(null);
        setEditText('');
      }
    } catch (error) {
      console.error('Error editing message:', error);
      alert('Failed to edit message. Please try again.');
    }
  };

  const handleDeleteMessage = (messageId: number) => {
    setDeleteMessageId(messageId);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteMessageId) return;

    const messageIdToDelete = deleteMessageId;
    setOpenDeleteDialog(false);
    setDeleteMessageId(null);

    try {
      await apiRequest(`/messages/${messageIdToDelete}`, {
        method: 'DELETE',
      });

      // Remove message locally (socket will also broadcast to other users)
      setMessages((prev) => prev.filter((msg) => msg.id !== messageIdToDelete));
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message. Please try again.');
    }
  };

  // Get full file URL from relative path, with token in query parameter
  const getFileUrl = (fileUrl: string | null | undefined): string => {
    if (!fileUrl) return '';
    // If it's already a full URL (base64 or http), return as is
    if (fileUrl.startsWith('data:') || fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      return fileUrl;
    }
    
    // If it's a relative path, prepend API base URL and add token
    const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';
    let fullUrl: string;
    
    // If using relative URL (proxy), use it directly (already properly encoded by backend)
    if (fileUrl.startsWith('/api/')) {
      fullUrl = fileUrl;
    } else if (fileUrl.startsWith('/')) {
      fullUrl = `${API_BASE_URL}${fileUrl}`;
    } else {
      fullUrl = `${API_BASE_URL}/${fileUrl}`;
    }
    
    return fullUrl;
  };

  // Check if file URL is an image
  const isImageFile = (fileUrl: string | null | undefined): boolean => {
    if (!fileUrl) return false;
    const url = fileUrl.toLowerCase();
    return url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || 
           url.includes('.gif') || url.includes('.webp') || url.startsWith('data:image/');
  };

  // Get original filename from URL
  const getOriginalFilename = (fileUrl: string | null | undefined): string => {
    if (!fileUrl) return 'file';
    try {
      const url = new URL(fileUrl, window.location.origin);
      const originalName = url.searchParams.get('originalName');
      if (originalName) {
        return decodeURIComponent(originalName);
      }
      // Fallback to extracting from path
      const parts = fileUrl.split('/');
      return parts[parts.length - 1].split('?')[0];
    } catch {
      const parts = fileUrl.split('/');
      return parts[parts.length - 1].split('?')[0];
    }
  };

  // Handle file download
  const handleDownloadFile = async (fileUrl: string | null | undefined) => {
    if (!fileUrl) return;
    
    try {
      const url = getFileUrl(fileUrl);

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = getOriginalFilename(fileUrl);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '600px',
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Paper
        elevation={2}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
          border: isDragOver ? '2px dashed' : 'none',
          borderColor: isDragOver ? 'primary.main' : 'transparent',
          bgcolor: isDragOver ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        {isDragOver && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(25, 118, 210, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                p: 3,
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: 3,
              }}
            >
              <ImageIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" color="primary.main">
                Drop files here to upload
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Release to add file to chat
              </Typography>
            </Box>
          </Box>
        )}
        {/* Messages area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
              <Typography variant="body2">No messages yet. Start the conversation!</Typography>
            </Box>
          ) : (
            messages.map((message) => {
              const isOwnMessage = message.sender_id === currentUserId;
              return (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: isOwnMessage ? 'row-reverse' : 'row',
                      alignItems: 'flex-start',
                      gap: 1,
                      maxWidth: '70%',
                    }}
                  >
                    {!isOwnMessage && (
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: 'primary.main',
                          fontSize: '0.75rem',
                        }}
                      >
                        {getInitials(message.sender_name)}
                      </Avatar>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
                      }}
                    >
                      {!isOwnMessage && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography variant="caption" fontWeight="bold">
                            {message.sender_name || 'Unknown'}
                          </Typography>
                          {message.sender_role && (
                            <Chip
                              label={message.sender_role}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      )}
                      <Paper
                        elevation={1}
                        sx={{
                          p: 1.5,
                          bgcolor: isOwnMessage ? 'primary.main' : (isDark ? 'grey.800' : 'grey.100'),
                          color: isOwnMessage ? 'primary.contrastText' : 'text.primary',
                          borderRadius: 2,
                          maxWidth: '100%',
                          position: 'relative',
                        }}
                      >
                        {message.image_url && (
                          <>
                            {isImageFile(message.image_url) ? (
                              <Box
                                sx={{
                                  mb: message.message_text ? 1 : 0,
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  maxWidth: '300px',
                                  maxHeight: '300px',
                                  cursor: 'pointer',
                                }}
                                onClick={() => setModalImage(message.image_url || null)}
                              >
                                <img
                                  src={getFileUrl(message.image_url)}
                                  alt="Shared image"
                                  style={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block',
                                    objectFit: 'contain',
                                  }}
                                  onError={(e) => {
                                    console.error('Failed to load image:', message.image_url);
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  mb: message.message_text ? 1 : 0,
                                  p: 1.5,
                                  borderRadius: 1,
                                  bgcolor: 'action.hover',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  maxWidth: '300px',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    bgcolor: 'action.selected',
                                  },
                                }}
                                onClick={() => handleDownloadFile(message.image_url)}
                              >
                                <AttachFileIcon sx={{ color: 'primary.main' }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 'medium',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {getOriginalFilename(message.image_url)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Click to download
                                  </Typography>
                                </Box>
                                <DownloadIcon sx={{ color: 'primary.main' }} />
                              </Box>
                            )}
                          </>
                        )}
                        {editingMessageId === message.id ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField
                              multiline
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              variant="outlined"
                              size="small"
                              sx={{
                                bgcolor: 'background.paper',
                                '& .MuiOutlinedInput-root': {
                                  color: 'text.primary',
                                },
                              }}
                              autoFocus
                            />
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <IconButton
                                size="small"
                                onClick={handleSaveEdit}
                                sx={{
                                  color: 'success.main',
                                  bgcolor: 'success.light',
                                  '&:hover': { bgcolor: 'success.main', color: 'white' },
                                }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                sx={{
                                  color: 'error.main',
                                  bgcolor: 'error.light',
                                  '&:hover': { bgcolor: 'error.main', color: 'white' },
                                }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : (
                          <>
                            {message.message_text && (
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {message.message_text}
                              </Typography>
                            )}
                          </>
                        )}
                      </Paper>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatTime(message.created_at)}
                        </Typography>
                        {message.updated_at !== message.created_at && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                            (edited)
                          </Typography>
                        )}
                        {isOwnMessage && (
                          <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                            {message.message_text && (
                              <IconButton
                                size="small"
                                onClick={() => handleEditMessage(message)}
                                sx={{
                                  p: 0.5,
                                  minWidth: 24,
                                  minHeight: 24,
                                  color: 'text.secondary',
                                  '&:hover': { 
                                    color: 'primary.main',
                                    bgcolor: 'action.hover',
                                  },
                                  transition: 'all 0.2s',
                                }}
                                title="Edit message"
                              >
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteMessage(message.id)}
                              sx={{
                                p: 0.5,
                                minWidth: 24,
                                minHeight: 24,
                                color: 'text.secondary',
                                '&:hover': { 
                                  color: 'error.main',
                                  bgcolor: 'error.light',
                                },
                                transition: 'all 0.2s',
                              }}
                              title="Delete message"
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    {isOwnMessage && (
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: 'secondary.main',
                          fontSize: '0.75rem',
                        }}
                      >
                        {getInitials('You')}
                      </Avatar>
                    )}
                  </Box>
                </Box>
              );
            })
          )}

          {/* Typing indicator */}
          {typingUsers.size > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {typingUsers.size === 1 ? 'Someone is typing...' : 'Multiple people are typing...'}
              </Typography>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        <Divider />

        {/* Input area */}
        <Box sx={{ p: 2 }}>
          {/* File preview */}
          {(filePreview || selectedFile) && (
            <Box
              sx={{
                mb: 1,
                position: 'relative',
                display: 'inline-block',
                maxWidth: '200px',
              }}
            >
              {filePreview ? (
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <img
                    src={filePreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      maxHeight: '150px',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveFile}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : selectedFile ? (
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1.5,
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AttachFileIcon sx={{ color: 'primary.main' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'medium',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={handleRemoveFile}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'error.main',
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : null}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <input
              type="file"
              accept="*/*"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={!isConnected || sending}
              color="primary"
              sx={{
                bgcolor: 'grey.100',
                '&:hover': {
                  bgcolor: 'grey.200',
                },
              }}
              title="Attach file"
            >
              <AttachFileIcon />
            </IconButton>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <TextField
                inputRef={textFieldRef}
                fullWidth
                multiline
                maxRows={4}
                placeholder={selectedFile ? "Add a caption (optional)..." : "Type a message... (use @ to mention)"}
                value={newMessage}
                onChange={handleMessageChange}
                onKeyDown={handleKeyPress}
                disabled={!isConnected || sending}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              {showMentions && (
                <ClickAwayListener onClickAway={() => setShowMentions(false)}>
                  <Popper
                    open={showMentions}
                    anchorEl={mentionAnchorEl}
                    placement="top-start"
                    sx={{
                      zIndex: 1300,
                      mt: 1,
                      maxHeight: 200,
                      overflow: 'auto',
                      width: 300,
                    }}
                  >
                    <Paper elevation={3}>
                      <List dense sx={{ p: 0 }}>
                        {getFilteredParticipants().length === 0 ? (
                          <ListItem>
                            <ListItemText
                              primary="No users found"
                              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                            />
                          </ListItem>
                        ) : (
                          getFilteredParticipants().map((participant, index) => (
                            <ListItem
                              key={participant.user_id}
                              disablePadding
                            >
                              <ListItemButton
                                selected={index === mentionIndex}
                                onClick={() => {
                                  insertMention(index);
                                }}
                                sx={{
                                  '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                      bgcolor: 'primary.dark',
                                    },
                                  },
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: index === mentionIndex ? 'primary.contrastText' : 'primary.main',
                                      color: index === mentionIndex ? 'primary.main' : 'primary.contrastText',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {getInitials(participant.name || participant.email)}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={participant.name || participant.email}
                                  secondary={participant.email !== participant.name ? participant.email : participant.user_role}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: index === mentionIndex ? 'bold' : 'normal',
                                  }}
                                  secondaryTypographyProps={{
                                    variant: 'caption',
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))
                        )}
                      </List>
                    </Paper>
                  </Popper>
                </ClickAwayListener>
              )}
            </Box>
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={(!newMessage.trim() && !selectedFile) || sending || !isConnected}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              {sending ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </Box>
          {!isConnected && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              Connecting to server...
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Image Modal */}
      <Modal
        open={!!modalImage}
        onClose={() => setModalImage(null)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={!!modalImage}>
          <Box
            sx={{
              position: 'relative',
              outline: 'none',
              maxWidth: '90vw',
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 2,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              onClick={() => setModalImage(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                zIndex: 1,
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            {modalImage && (
              <img
                src={getFileUrl(modalImage)}
                alt="Full size"
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  display: 'block',
                  borderRadius: 4,
                }}
                onError={(e) => {
                  console.error('Failed to load image in modal:', modalImage);
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setDeleteMessageId(null);
        }}
      >
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this message? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDeleteDialog(false);
            setDeleteMessageId(null);
          }}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
